/**
 * Competition registration rules — the ONE copy the button the entrant sees
 * and the boundary the server enforces both run.
 *
 * Five rules decide whether somebody may enter a competition and under what
 * number: when the window is open (in the GYM's clock), which guest mode is
 * on, how many guests one person may bring, which bib number is next, and
 * whether a session still has room. Each used to exist twice — once in the
 * web bundle rendering the "+ Vendég" button and the "1 / 2" hint, once in
 * the Functions refusing the callable — held together by two parity suites. A drift there
 * never threw; it produced the worst possible UX: a button offered and then
 * refused, a live "Register" button in London for an hour after the server
 * had started refusing. One copy ends that by construction.
 *
 * What stays per side: the client's `getRegistrationState` (the UX shape of
 * the same four window conditions) and the server's `registrationWindowError`
 * (the boundary, with its stable error codes), both built on `compDateMillis`
 * from here; and the server's `sessionOccupancy` (a count over a registration
 * list) feeding `sessionIsFull`.
 */

// ── The gym's clock ──────────────────────────────────────────────────────────

/**
 * The zone a competition's zone-less dates are read in when the gym has not
 * set one. Every gym on the platform today is Hungarian, and this is what the
 * server always assumed before `gyms/{gymId}.timezone` was threaded through —
 * so a gym with no timezone keeps exactly the behaviour it had.
 */
export const DEFAULT_COMP_TIMEZONE = "Europe/Budapest";

/**
 * How far `timezone` is from UTC at a given instant (DST-correct), in ms.
 * Same approach the scheduled jobs use (see `apps/functions/scheduleSlots.js`).
 *
 * An unknown or malformed timezone string degrades to the default zone rather
 * than throwing: a typo in one gym's settings must not turn every registration
 * into an INTERNAL error, nor blank the Verseny tab.
 */
export function zoneOffsetMs(utcMillis, timezone) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || DEFAULT_COMP_TIMEZONE, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(new Date(utcMillis));
  } catch (_) {
    if (!timezone || timezone === DEFAULT_COMP_TIMEZONE) throw new Error("default timezone unavailable");
    return zoneOffsetMs(utcMillis, DEFAULT_COMP_TIMEZONE);
  }
  const p = parts.reduce((acc, part) => { acc[part.type] = part.value; return acc; }, {});
  const asUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour) % 24, Number(p.minute), Number(p.second)
  );
  return asUtc - utcMillis;
}

/**
 * Milliseconds for a competition date field, or null when absent/unusable.
 *
 * The admin editor stores `registrationStart` / `registrationDeadline` as
 * `datetime-local` strings ("2026-08-10T14:00") that carry no zone. The
 * browser used to read them as ITS OWN local wall-clock and Cloud Functions
 * run in UTC, so the button and the boundary disagreed for anyone whose
 * browser was not on the gym's clock — and for everyone, two hours, in
 * summer. Both sides anchor a zone-less value to the GYM's timezone
 * (`gyms/{gymId}.timezone`, `DEFAULT_COMP_TIMEZONE` when unset). A value
 * that carries its own zone ("…Z", "…+05:00") is honoured as written.
 * Firestore Timestamps (`toMillis` / `toDate`) and Dates pass through. Never NaN.
 */
export function compDateMillis(value, timezone) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value !== "string") return null;
  const naive = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/.test(value);
  if (!naive) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const asIfUtc = Date.parse(value.includes("T") ? `${value}Z` : `${value}T00:00Z`);
  if (Number.isNaN(asIfUtc)) return null;
  return asIfUtc - zoneOffsetMs(asIfUtc, timezone);
}

// ── Guests and start numbers ─────────────────────────────────────────────────

export const GUEST_MODES = { OFF: "off", ORGANIZER: "organizer", ANYONE: "anyone" };
export const DEFAULT_PROXY_GUEST_LIMIT = 2;
export const PROXY_GUEST_LIMIT_MIN = 1;
export const PROXY_GUEST_LIMIT_MAX = 20;

/**
 * The guest mode of a competition.
 *
 * Competitions written before the three-state setting carry only the
 * `allowGuests` boolean, which meant "anyone may bring a guest" — map it
 * forward so an existing competition keeps behaving exactly as it did.
 */
export function resolveGuestMode(comp) {
  const mode = comp && comp.guestMode;
  if (mode === GUEST_MODES.OFF || mode === GUEST_MODES.ORGANIZER || mode === GUEST_MODES.ANYONE) return mode;
  return comp && comp.allowGuests === true ? GUEST_MODES.ANYONE : GUEST_MODES.OFF;
}

/**
 * How many guests one person may bring to THIS competition.
 *
 * Applies to everyone, gym admins and organisers included. They used to be
 * exempt, which made the editor's own "max. N" hint read as a lie on their card
 * ("1/∞"). Staff needing more do not need an exemption: the participants editor
 * registers guests with no proxy owner, and that path is unlimited.
 */
export function resolveProxyGuestLimit(comp) {
  const raw = comp && comp.proxyGuestLimit;
  if (raw === null || raw === undefined || raw === "") return DEFAULT_PROXY_GUEST_LIMIT;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return DEFAULT_PROXY_GUEST_LIMIT;
  return Math.min(PROXY_GUEST_LIMIT_MAX, Math.max(PROXY_GUEST_LIMIT_MIN, n));
}

/**
 * Smallest positive integer start number not present in `usedNumbers`.
 *
 * A number freed by a cancelled registration is handed out again, so the
 * organiser prints as few bib numbers as possible. `usedNumbers` may be a Set,
 * an array, or any iterable; non-positive and non-integer entries are ignored.
 */
export function lowestFreeStartNumber(usedNumbers) {
  const used = new Set();
  for (const v of usedNumbers || []) {
    if (Number.isInteger(v) && v > 0) used.add(v);
  }
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

// ── Session capacity — is this run full for THIS person? ─────────────────────

/**
 * Free places in a session FOR THIS VIEWER. `taken` counts every competitor
 * in the session (the client's count() query includes the viewer's own entry
 * when they hold one); `ownSlot` hands that one place back. Before D5 the
 * client answered with an `ownSlot ? never full` shortcut, which disagreed
 * with the server on an over-booked session: the viewer read "room" and the
 * callable refused.
 */
export function sessionFreeSlots({ max, taken, ownSlot = false }) {
  return (Number(max) || 0) - (Number(taken) || 0) + (ownSlot ? 1 : 0);
}

/**
 * Is a session full for a caller whose own entry (if any) has already been
 * left out of `occupancyExcludingSelf`? Uncapped sessions are never full.
 * The same rule as `sessionFreeSlots` — the server counts everyone but the
 * caller and asks this; the client counts everyone and hands the caller's
 * place back — so the two can only agree.
 */
export function sessionIsFull(maxParticipants, occupancyExcludingSelf) {
  const max = Number(maxParticipants) || 0;
  return max > 0 && sessionFreeSlots({ max, taken: occupancyExcludingSelf }) <= 0;
}
